import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  IconButton,
  Flex,
  VStack,
  HStack,
  Badge,
  Icon,
  Card,
  CardBody,
  Tooltip,
  useToast,
  useColorModeValue,
  Divider,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Tag,
  TagLabel,
  TagLeftIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  ScaleFade,
  useBreakpointValue,
  SimpleGrid,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Slide,
} from '@chakra-ui/react';
import { FaVolumeUp, FaLanguage, FaTrash, FaArrowLeft, FaEllipsisV, FaBookmark, FaHeadphones, FaSave, FaCheck } from 'react-icons/fa';
import { FiBookOpen, FiCalendar, FiClock, FiVolume2, FiGlobe, FiTrash2, FiHelpCircle, FiX, FiMenu } from 'react-icons/fi';
import { getArticleById, deleteArticle, saveWordToCollection, markWordAsKnown, ArticleWordTranslation, getWordTranslation } from '../services/smartReadApi';
import { WordPopup } from '../components/smart-read/WordPopup';
import { WordTranslationProvider } from '../components/common/WordTranslationProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { playTTS } from '../services/ttsApi';
import { useTranslation } from 'react-i18next';

const MotionBox = motion(Box);

export function SmartReadArticle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userLanguage } = useLanguage();
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen: isWordDrawerOpen, onOpen: onWordDrawerOpen, onClose: onWordDrawerClose } = useDisclosure();
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const { t } = useTranslation();

  // All color mode hooks at the top - called unconditionally
  const bgGradient = useColorModeValue(
    'linear(to-b, purple.50, white)',
    'linear(to-b, purple.900, gray.900)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('purple.100', 'purple.700');
  const highlightColor = useColorModeValue('purple.500', 'purple.300');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryText = useColorModeValue('gray.600', 'gray.400');
  const accentButtonBg = useColorModeValue('green.500', 'green.400');
  const accentButtonHoverBg = useColorModeValue('green.600', 'green.500');
  const wordHighlightBg = useColorModeValue('purple.50', 'purple.800');
  const wordHighlightHoverBg = useColorModeValue('purple.100', 'purple.700');
  const tooltipBg = useColorModeValue('gray.700', 'gray.200');
  const tooltipColor = useColorModeValue('white', 'gray.800');
  const mobileBg = useColorModeValue('white', 'gray.800');
  const mobileHeaderBg = useColorModeValue('rgba(255, 255, 255, 0.9)', 'rgba(26, 32, 44, 0.9)');
  const backButtonHoverBg = useColorModeValue('purple.50', 'purple.900');
  const helperCardBg = useColorModeValue('purple.50', 'gray.700');
  const helperIconBoxBg = useColorModeValue('white', 'gray.800');

  // Responsive design hooks
  const isMobile = useBreakpointValue({ base: true, md: false });
  const contentPadding = isMobile ? 4 : 8;
  const fontSize = isMobile ? "md" : "lg";

  // Refs
  const wordDrawerRef = useRef(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // State
  const [headerHeight, setHeaderHeight] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const [isPlayingArticle, setIsPlayingArticle] = useState(false);
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);

  // Fetch article data
  const { data, isLoading, error } = useQuery({
    queryKey: ['article', id],
    queryFn: () => getArticleById(id!),
    enabled: !!id,
  });

  // Memoize the article language to ensure consistent hook calls
  const articleLanguage = useMemo(() => data?.article?.language || userLanguage, [data?.article?.language, userLanguage]);

  // Determine if current user is the article creator
  const isCreator = useMemo(() => {
    if (!data?.article || !user) return false;
    return data.article.user_id === user.id;
  }, [data?.article, user]);

  // Delete article mutation
  const { mutate: deleteArticleMutation } = useMutation({
    mutationFn: deleteArticle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      toast({
        title: t('smartRead.articleDeleted'),
        description: t('smartRead.articleDeletedMessage'),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      navigate('/smart-read');
    },
  });

  // Measure the height of the header for proper content positioning
  useEffect(() => {
    if (headerRef.current && isMobile) {
      const height = headerRef.current.getBoundingClientRect().height;
      setHeaderHeight(height);
    }
  }, [headerRef, isMobile]);

  // Handle scroll for mobile header visibility
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const visible = lastScrollPosition > currentScrollPos || currentScrollPos < 10;

      setHeaderVisible(visible);
      setLastScrollPosition(currentScrollPos);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollPosition, isMobile]);

  // Toggle translation
  const toggleTranslation = () => {
    setShowTranslation(!showTranslation);
  };

  // Handle playing the entire article
  const handlePlayArticle = async () => {
    if (isPlayingArticle) return;

    try {
      setIsPlayingArticle(true);
      await playTTS(getContent(), articleLanguage);
    } catch (error) {
      console.error('Error playing article:', error);
      toast({
        title: t('smartRead.playbackError'),
        description: t('smartRead.unableToPlayArticle'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsPlayingArticle(false);
    }
  };

  // Handle delete article
  const handleDeleteArticle = () => {
    onDeleteDialogOpen();
  };

  // Confirm delete article
  const confirmDeleteArticle = () => {
    deleteArticleMutation(id!);
    onDeleteDialogClose();
  };

  // Cancel delete article
  const cancelDeleteArticle = () => {
    onDeleteDialogClose();
  };

  // Close all popups when clicking outside
  const handleClosePopups = () => {
    // WordTranslationProvider handles its own popup closing
  };

  // Get the appropriate content based on language and translation setting
  const getContent = useCallback(() => {
    if (!data) return '';

    if (showTranslation) {
      // Log that we're trying to show a translation
      console.log("Attempting to show translation in language:", userLanguage);

      // Safety check for translations array
      if (!data.translations) {
        console.error("No translations array found in data:", data);
        return data.article.content;
      }

      // Find user's language translation or fallback appropriately
      const languageCode = {
        'english': 'en',
        'dutch': 'nl',
        'hebrew': 'he'
      }[userLanguage.toLowerCase()] || userLanguage;

      console.log("Looking for translation with language code:", languageCode);

      // Try both by code and by full name
      const translation = data.translations.find(
        t => t.language === languageCode || t.language === userLanguage
      );

      if (!translation) {
        console.warn(`No translation found for language: ${userLanguage}`);
        // Try to find an English translation as fallback
        const englishTranslation = data.translations.find(
          t => t.language === 'en' || t.language === 'english'
        );
        if (englishTranslation) {
          console.log("Using English translation as fallback");
          return englishTranslation.content;
        }
        // If no English translation, use the original content
        console.log("Using original content as last resort");
        return data.article.content;
      }

      console.log("Found translation:", translation);
      return translation.content || data.article.content;
    }

    return data.article.content;
  }, [data, showTranslation, userLanguage]);

  // Format content with clickable words
  const formatContent = (content: string) => {
    // This is now handled by WordTranslationProvider
    return content;
  };

  if (isLoading) {
    return (
      <Box
        minH="calc(100vh - 70px)"
        bgGradient={bgGradient}
        py={8}
        px={4}
      >
        <Container maxW="container.lg">
          <Card bg={cardBg} shadow="md" borderRadius="xl" minH="300px">
            <Flex direction="column" align="center" justify="center" py={10}>
              <Spinner size="xl" thickness="4px" color={highlightColor} />
              <Text mt={4} color={secondaryText}>{t('smartRead.loadingArticle')}</Text>
            </Flex>
          </Card>
        </Container>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box minH="calc(100vh - 70px)" bgGradient={bgGradient} py={8} px={4}>
        <Container maxW="container.lg">
          <Card bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{t('smartRead.errorLoading')}</AlertDescription>
            </Alert>
            <Flex justify="center" mt={6}>
              <Button
                onClick={() => navigate('/smart-read')}
                colorScheme="purple"
                leftIcon={<Icon as={FaArrowLeft} />}
              >
                {t('smartRead.backToArticles')}
              </Button>
            </Flex>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      minH="calc(100vh - 70px)"
      bgGradient={isMobile ? 'none' : bgGradient}
      bg={isMobile ? mobileBg : 'transparent'}
      py={isMobile ? 0 : 8}
      px={isMobile ? 0 : 4}
      position="relative"
      onClick={handleClosePopups}
    >
      {/* Mobile fixed header */}
      {isMobile && (
        <Slide direction="top" in={headerVisible} style={{ zIndex: 10 }}>
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bg={mobileHeaderBg}
            boxShadow="0 2px 10px rgba(0,0,0,0.05)"
            backdropFilter="blur(8px)"
            zIndex={10}
            ref={headerRef}
          >
            <Flex
              justify="space-between"
              align="center"
              p={3}
              borderBottomWidth="1px"
              borderBottomColor={cardBorder}
            >
              <IconButton
                aria-label={t('smartRead.backToArticles')}
                icon={<FaArrowLeft />}
                variant="ghost"
                onClick={() => navigate('/smart-read')}
                fontSize="20px"
                color={textColor}
              />

              <Text
                fontWeight="bold"
                fontSize="md"
                noOfLines={1}
                maxW="60%"
                textAlign="center"
              >
                {data.article.topic}
              </Text>

              <HStack spacing={1}>
                <IconButton
                  aria-label={showTranslation ? t('smartRead.showOriginalText') : t('smartRead.showTranslationText')}
                  icon={<FaLanguage />}
                  onClick={toggleTranslation}
                  variant="ghost"
                  size="sm"
                  fontSize="18px"
                  color={textColor}
                />
                <IconButton
                  aria-label={isPlayingArticle ? t('smartRead.playing') : t('smartRead.playArticle')}
                  icon={<FaHeadphones />}
                  onClick={handlePlayArticle}
                  isDisabled={isPlayingArticle}
                  variant="ghost"
                  size="sm"
                  fontSize="18px"
                  color={textColor}
                />
                {isCreator && (
                  <IconButton
                    aria-label={t('smartRead.deleteArticle')}
                    icon={<FaTrash />}
                    onClick={handleDeleteArticle}
                    variant="ghost"
                    size="sm"
                    fontSize="18px"
                    color="red.500"
                  />
                )}
              </HStack>
            </Flex>

            <Flex px={3} py={2} justify="space-between" align="center">
              <HStack>
                <Tag size="sm" variant="subtle" colorScheme="purple">
                  {data.article.length === 'short' ? t('smartRead.short') : t('smartRead.long')}
                </Tag>
                <Tag size="sm" variant="subtle" colorScheme="blue">
                  {data.article.language
                    ? data.article.language.charAt(0).toUpperCase() + data.article.language.slice(1)
                    : userLanguage.charAt(0).toUpperCase() + userLanguage.slice(1)}
                </Tag>
              </HStack>

              <Text fontSize="xs" color={secondaryText}>
                {new Date(data.article.created_at).toLocaleDateString()}
              </Text>
            </Flex>
          </Box>
        </Slide>
      )}

      <Container
        maxW="container.lg"
        pt={isMobile ? `${headerHeight + 16}px` : 0}
        pb={isMobile ? 6 : 0}
        px={isMobile ? 3 : 4}
      >
        {/* Desktop header */}
        {!isMobile && (
          <ScaleFade initialScale={0.95} in={true}>
            {/* Header with navigation and title */}
            <Flex
              direction="column"
              mb={6}
              position="relative"
            >
              <Button
                variant="ghost"
                leftIcon={<Icon as={FaArrowLeft} />}
                onClick={() => navigate('/smart-read')}
                mb={4}
                alignSelf="flex-start"
                color={highlightColor}
                _hover={{ bg: backButtonHoverBg }}
              >
                {t('smartRead.backToArticles')}
              </Button>

              <Flex
                justifyContent="space-between"
                alignItems="flex-start"
                wrap="wrap"
                gap={4}
              >
                <VStack align="flex-start" spacing={2} flex="1">
                  <Heading size="xl" color={textColor} fontWeight="bold">
                    {data.article.topic}
                  </Heading>

                  <HStack spacing={3} wrap="wrap">
                    <Tag size="md" variant="subtle" colorScheme="purple">
                      <TagLeftIcon as={FiClock} />
                      <TagLabel>{data.article.length === 'short' ? t('smartRead.shortArticle') : t('smartRead.longArticle')}</TagLabel>
                    </Tag>

                    <Tag size="md" variant="subtle" colorScheme="gray">
                      <TagLeftIcon as={FiCalendar} />
                      <TagLabel>{new Date(data.article.created_at).toLocaleDateString()}</TagLabel>
                    </Tag>

                    <Tag size="md" variant="subtle" colorScheme="blue">
                      <TagLeftIcon as={FiGlobe} />
                      <TagLabel>
                        {data.article.language
                          ? data.article.language.charAt(0).toUpperCase() + data.article.language.slice(1)
                          : userLanguage.charAt(0).toUpperCase() + userLanguage.slice(1)}
                      </TagLabel>
                    </Tag>
                  </HStack>
                </VStack>

                {/* Desktop Action buttons */}
                <HStack spacing={3}>
                  <Tooltip label={showTranslation ? t('smartRead.showOriginalText') : t('smartRead.showTranslationText')} bg={tooltipBg} color={tooltipColor}>
                    <Button
                      leftIcon={<Icon as={FaLanguage} />}
                      onClick={toggleTranslation}
                      variant="outline"
                      colorScheme="purple"
                      data-testid="translation-toggle"
                    >
                      {showTranslation ? t('smartRead.showOriginal') : t('smartRead.showTranslation')}
                    </Button>
                  </Tooltip>

                  <Tooltip label={t('smartRead.listenToArticle')} bg={tooltipBg} color={tooltipColor}>
                    <Button
                      leftIcon={<Icon as={FaVolumeUp} />}
                      onClick={handlePlayArticle}
                      isDisabled={isPlayingArticle}
                      colorScheme="blue"
                      variant="outline"
                    >
                      {isPlayingArticle ? t('smartRead.playing') : t('smartRead.playArticle')}
                    </Button>
                  </Tooltip>

                  {isCreator && (
                    <Tooltip label={t('smartRead.deleteArticle')} bg={tooltipBg} color={tooltipColor}>
                      <Button
                        leftIcon={<Icon as={FaTrash} />}
                        onClick={handleDeleteArticle}
                        colorScheme="red"
                        variant="outline"
                      >
                        {t('smartRead.delete')}
                      </Button>
                    </Tooltip>
                  )}
                </HStack>
              </Flex>
            </Flex>
          </ScaleFade>
        )}

        {/* Main content card */}
        <Card
          bg={cardBg}
          borderRadius={isMobile ? "lg" : "xl"}
          shadow="md"
          borderWidth="1px"
          borderColor={cardBorder}
          mb={6}
          overflow="hidden"
        >
          <CardBody p={contentPadding}>
            <WordTranslationProvider
              sourceLanguage={articleLanguage}
              preloadedTranslations={data?.wordTranslations || []}
            >
              {(formatContent) => (
                <Box
                  fontSize={fontSize}
                  lineHeight="tall"
                  color={textColor}
                  letterSpacing="wide"
                  className="article-content"
                  dir={
                    showTranslation
                      ? (userLanguage?.toLowerCase() === 'hebrew' ? 'rtl' : 'ltr')
                      : (data.article.language?.toLowerCase() === 'hebrew' ? 'rtl' : 'ltr')
                  }
                >
                  {formatContent(getContent())}
                </Box>
              )}
            </WordTranslationProvider>
          </CardBody>
        </Card>

        {/* Helper card - only show on desktop or bottom of mobile */}
        <Card
          bg={helperCardBg}
          borderRadius={isMobile ? "lg" : "xl"}
          p={contentPadding}
          mx={isMobile ? 2 : 0}
          mb={isMobile ? 8 : 0}
        >
          <Flex align="center" mb={4}>
            <Icon as={FiHelpCircle} fontSize="xl" color={highlightColor} mr={2} />
            <Heading size="md" color={textColor}>{t('smartRead.howToUse')}</Heading>
          </Flex>

          <Divider mb={4} />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <HStack align="flex-start" spacing={3}>
              <Box
                p={2}
                bg={helperIconBoxBg}
                borderRadius="md"
                color={highlightColor}
              >
                <Icon as={FiBookOpen} fontSize="xl" />
              </Box>
              <Text color={secondaryText}>
                {t('smartRead.howToUseWord')}
              </Text>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box
                p={2}
                bg={helperIconBoxBg}
                borderRadius="md"
                color={highlightColor}
              >
                <Icon as={FiVolume2} fontSize="xl" />
              </Box>
              <Text color={secondaryText}>
                {t('smartRead.howToUseSound')}
              </Text>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box
                p={2}
                bg={helperIconBoxBg}
                borderRadius="md"
                color={highlightColor}
              >
                <Icon as={FaBookmark} fontSize="lg" />
              </Box>
              <Text color={secondaryText}>
                {t('smartRead.howToUseSave')}
              </Text>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box
                p={2}
                bg={helperIconBoxBg}
                borderRadius="md"
                color={highlightColor}
              >
                <Icon as={FaLanguage} fontSize="xl" />
              </Box>
              <Text color={secondaryText}>
                {t('smartRead.howToUseToggle')}
              </Text>
            </HStack>
          </SimpleGrid>
        </Card>
      </Container>

      {/* Delete Article Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={cancelDeleteArticle}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('smartRead.deleteConfirm.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('smartRead.deleteConfirm.message')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelDeleteArticle}>
                {t('smartRead.deleteConfirm.cancel')}
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteArticle} ml={3}>
                {t('smartRead.deleteConfirm.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}